import { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, Search, Check } from 'lucide-react'

// Select com BUSCA, reutilizável em todo o sistema.
// options: [{value, label}] ou [string]. onChange recebe o VALOR (não evento).
// A caixa de busca aparece automaticamente quando há mais de `minSearch` opções.
export default function SearchableSelect({
  value, onChange, options = [], placeholder = 'Selecione...',
  emptyOption = true, emptyLabel, disabled = false, className = '', minSearch = 6,
}) {
  const [open, setOpen] = useState(false)
  const [up, setUp] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)

  const toggle = () => {
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect()
      setUp(window.innerHeight - r.bottom < 280)
    }
    setOpen((o) => !o)
  }

  const norm = useMemo(
    () => options.map((o) => (o && typeof o === 'object' ? { value: o.value, label: String(o.label ?? o.value) } : { value: o, label: String(o) })),
    [options],
  )

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQ('') } }
    document.addEventListener('mousedown', fn)
    return () => document.removeEventListener('mousedown', fn)
  }, [])

  const sel = norm.find((o) => String(o.value) === String(value))
  const showSearch = norm.length > minSearch
  const filtered = useMemo(() => {
    if (!q) return norm
    const t = q.toLowerCase()
    return norm.filter((o) => o.label.toLowerCase().includes(t))
  }, [q, norm])

  const pick = (v) => { onChange(v); setOpen(false); setQ('') }

  return (
    <div className={`relative ${className}`} ref={ref}>
      <button type="button" disabled={disabled} onClick={toggle}
        className="w-full flex items-center justify-between gap-2 border border-gray-200 rounded-lg px-3 py-2 text-sm text-left bg-white outline-none focus:border-accent-400 hover:border-gray-300 disabled:opacity-60">
        <span className={`truncate ${sel ? 'text-gray-800' : 'text-gray-400'}`}>{sel ? sel.label : placeholder}</span>
        <ChevronDown size={16} className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className={`absolute z-50 left-0 right-0 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden ${up ? 'bottom-full mb-1' : 'top-full mt-1'}`}>
          {showSearch && (
            <div className="p-2 border-b border-gray-100 bg-gray-50">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar..."
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-accent-400" />
              </div>
            </div>
          )}
          <div className="max-h-60 overflow-y-auto tc-scroll py-1">
            {emptyOption && (
              <button type="button" onClick={() => pick('')}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${!value ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-500'}`}>
                {emptyLabel || placeholder}
              </button>
            )}
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-sm text-gray-400 text-center">Nada encontrado</p>
            ) : filtered.map((o) => (
              <button type="button" key={String(o.value)} onClick={() => pick(o.value)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-primary-50 ${String(o.value) === String(value) ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'}`}>
                <span className="truncate">{o.label}</span>
                {String(o.value) === String(value) && <Check size={14} className="text-accent-600 flex-shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
